import type {ReactElement, RefObject} from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import {SocialMediaWizardHeader} from '@Components/social-media-wizard/components/social-media-wizard-header/social-media-wizard-header';
import {SocialMediaWizardFooter} from '@Components/social-media-wizard/components/social-media-wizard-footer/social-media-wizard-footer';
import {SocialMediaWizardContent} from '@Components/social-media-wizard/components/social-media-wizard-content';
import {SocialAccountsList} from '@Components/social-media-wizard/components/social-accounts-list';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {
  getWizardContentSize,
  MY_STUFF_REDIRECT_LOADING_KEY,
  PUBLISH_PARAM_HANDLER_CLASS,
  SAVE_DRAFT_BEFORE_REDIRECT_DELAY_MS,
  SocialMediaWizardContentSize,
  SocialMediaWizardStep,
} from '@Components/social-media-wizard/social-media-wizard.types';
import type {WizardPanelDescriptor} from '@Components/social-media-wizard/social-media-wizard.types';
import {SocialAccountConnection} from '@Components/social-media-wizard/components/social-account-connection';
import {SocialAccountButtons} from '@Components/social-media-wizard/components/social-account-buttons';
import {
  useAccountIdsToPublishTo,
  useAccountsToPublishTo,
  useAccountTypeToConnect,
  useHasResolvedStartStep,
  useHaveAllPostsPublished,
  useHaveAllPostsSettled,
  useNextButtonText,
  usePublishedSlotCounts,
  usePublishingStatusForAllAccounts,
  useSocialMediaWizardPost,
  useSocialPostPublishingAction,
  useTriggerNext,
  useTriggerPrevious,
  useUpdatePublishingParams,
  useWizardStep,
} from '@Components/social-media/social-media.hooks';
import {getSchedulingDateForPrefill} from '@Libraries/social-media-wizard-scheduling-library';
import {getDesignGalleryHeightForSelectDesignStep, getKeyForPublishParams, getWizardUploadDesignsModalOpts, jumpContainerToTop, scrollToPublishParams, WIZARD_UPLOAD_MEDIA_GA4_CALLBACKS} from '@Libraries/social-media-wizard-design-library';
import {getNextButtonConfig, getNextWizardStep, getPublishingPanelHeading, getStartingURIForGallery, getWizardStepOrder, hasMobilePreviewAction} from '@Libraries/social-media-wizard-step-library';
import {exitWizardToOriginSurface, getWizardExitLabel, openSocialMediaWizardAuthenticateModal, redirectToMyStuffAfterDelay} from '@Libraries/social-media-wizard-library';
import {
  disableNextButton,
  enableNextButton,
  initPublishingParams,
  resetAccountResizeStatusForDesignsMap,
  resetSocialPostCoverForAllPublishingParams,
  resetStateToDraft,
  showNextButton,
  syncPublishingParams,
  updateDesignsToPublish,
  updateDesignsToPublishAndTriggerNext,
  updateIsTemplateSelected,
  updateNextButtonIcon,
  updateNextButtonText,
  updatePost,
  updatePublishingAction,
  updateTriggerNext,
  updateTriggerPrevious,
  updateWizardStep,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {SocialMediaPostDetails} from '@Components/social-media-wizard/components/social-media-post-details';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {
  useAreAccountsLoading,
  useAreCaptionsBeingAutoGenerated,
  usePostId,
  useDesignSelectorTab,
  useDesignsToPublish,
  useIsTemplateSelected,
  useSocialMediaPublishPhase,
  useSocialMediaWizardContainerQuery,
  useWizardCaptionErrors,
  useWizardConnectedAccounts,
  useWizardDesignsGraphicMetaMap,
  useWizardPublishingParams,
  useWizardTitleErrors,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {useWizardCoverCtaPulse, useWizardDraftScheduleActions} from '@Components/social-media-wizard/components/social-media-wizard-nav/social-media-wizard-nav.hooks';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {CircularProgressLoader} from '@Components/circular-progress-loader';
import {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import type {DesignSelection, SocialPlatform} from '@Components/social-media/social-media.types';
import {savePreferredProfileSelectionUser} from '@Components/social-media/social-media-thunk';
import {SocialMediaWizardOnboarding} from '@Components/social-media-wizard/components/social-media-wizard-onboarding';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {ShareSocialPostInTeamToggler} from '@Components/share-social-post-in-team-toggler';
import {GA4EventName, trackPublishingGA4Events} from '@Libraries/ga-events';
import {DesignGallery} from '@Components/design-gallery';
import type {GalleryPageURI} from '@Components/design-gallery/design-gallery.types';
import {TemplateEditorPanel} from '@Components/social-media-wizard/components/template-editor-panel';
import {WizardPreviewPanel} from '@Components/social-media-wizard/components/wizard-preview-panel/wizard-preview-panel';
import {openErrorModal} from '@Modals/error-modal';
import {convertDateToUTCTimestamp} from '@/libraries/social-media-post.library';
import {CACHE_KEY_FOR_SCHEDULING} from '@Libraries/social-media-grid.library';
import {getCaptionErrorAccountId, getTitleErrorAccountId} from '@Libraries/social-media-params.library';
import {goBackToEvent, goBackToEventFromOneClickPromotion, isEventOneClickPromotionFlow, isEventsSocialPostingFlow} from '@Libraries/social-media-event-promotion.library';
import {
  PREVIEW_GENERATION_POLLING_EVENT_ID,
  useAiCaptionWizardSetup,
  useDesignMetaSync,
  useEmbeddedEditorFlow,
  useHasReachedReviewStep,
  useTrackWizardStepViewed,
  useWizardInit,
  useWizardNavigationHistory,
  useWizardScrollState,
  useWizardWindowEvents,
} from '@Components/social-media-wizard/social-media-wizard-feature.hooks';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import {openSocialPostPreviewModal} from '@Modals/social-post-preview-modal/social-post-preview-modal.library';
import {openSocialPostAccountPreviewsModal} from '@Modals/social-post-account-previews-modal/social-post-account-previews-modal.library';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {didAllAccountsErrorOut, getAccountsAvailableForWizard} from '@Libraries/social-media-wizard-accounts-library';
import {SocialMediaPublishPhase} from '@Libraries/social-media-publish-phase.library';
import {WizardTerminalMobileFooter} from '@Components/social-media-wizard/components/wizard-terminal-mobile-footer/wizard-terminal-mobile-footer';
import {showLoading} from '@Libraries/loading-toast-library';
import styles from './social-media-wizard.module.scss';
import {useAppDispatch} from '@/hooks';
import type {SocialMediaAccountId, SocialPost} from '../social-media/post.vo';
import {PostState} from '../social-media/post.vo';
import '@Global/modal';
import {WizardDesignActions} from '@Components/social-media-wizard/components/wizard-design-actions/wizard-design-actions';
import {WizardDesignSelectorPanel} from '@Components/social-media-wizard/components/wizard-design-selector-panel/wizard-design-selector-panel';
import {openSocialMediaWizardAiChatModal} from '@Libraries/social-media-wizard-ai-chat-library';
import {AiChatSource} from '@Components/ai-chat-window/ai-chat-window.types';

const containerQuery: ContainerQueryObject = {
  xsmall: {
    minWidth: 0,
    maxWidth: 480,
  },
  small: {
    minWidth: 481,
    maxWidth: 783,
  },
  medium: {
    minWidth: 784,
    maxWidth: 900,
  },
  large: {
    minWidth: 901,
    maxWidth: 1149,
  },
  xlarge: {
    minWidth: 1150,
  },
};

interface SocialMediaWizardProps {
  post?: SocialPost;
  accounts?: SocialAccount[];
  accountIdsForPost?: number[];
  design?: DesignSelection;
  template?: DesignSelection;
  isGalleryFlow?: boolean;
  eventHashId?: string;
  numParentAccountsConnected?: number;
  hasSeenOnboarding?: boolean;
  hasSeenConfetti?: boolean;
  hasUserPublishedBefore?: boolean;
  isSocialMediaWizardAiCaptionsEnabled?: boolean;
  isGoogleBusinessProfileEnabled?: boolean;
  emphasizedPlatform?: SocialPlatform;
  galleryStartingURI?: GalleryPageURI | null;
  hasStartedLoggedOut?: boolean;
}

function isReferrerMyStuff(): boolean {
  const myStuffUrl = window.PMW.util.site_url('posters/mine');
  const referrerPath = document.referrer.split(/[?#]/)[0];

  return referrerPath === myStuffUrl || referrerPath.startsWith(`${myStuffUrl}/`);
}

function navigateOutOfWizard(): void {
  if (isEventsSocialPostingFlow()) {
    goBackToEvent();
    return;
  }

  if (isEventOneClickPromotionFlow()) {
    goBackToEventFromOneClickPromotion();
    return;
  }

  if (!document.referrer || window.PMW.isReferrer() || isReferrerMyStuff()) {
    window.location.href = window.PMW.util.site_url('posters/mine#/posts');
    return;
  }

  window.location.href = document.referrer;
}

export function SocialMediaWizard({
  post,
  accounts = [],
  accountIdsForPost = [],
  design,
  template,
  isGalleryFlow = false,
  eventHashId,
  numParentAccountsConnected = 0,
  hasSeenOnboarding = false,
  hasSeenConfetti = false,
  hasUserPublishedBefore = false,
  isSocialMediaWizardAiCaptionsEnabled = false,
  isGoogleBusinessProfileEnabled = false,
  emphasizedPlatform,
  galleryStartingURI = null,
  hasStartedLoggedOut = false,
}: SocialMediaWizardProps): ReactElement {
  const accountTypeToConnect = useAccountTypeToConnect();
  const wizardStep = useWizardStep();
  const hasResolvedStartStep = useHasResolvedStartStep();
  const stepOrder = getWizardStepOrder(hasStartedLoggedOut);
  const triggerNext = useTriggerNext();
  const nextButtonText = useNextButtonText();
  const wizardPost = useSocialMediaWizardPost();
  const isTemplateSelected = useIsTemplateSelected();
  const isSelectTemplateFlow = !isUserLoggedIn() || isTemplateSelected;
  const captionErrors = useWizardCaptionErrors();
  const titleErrors = useWizardTitleErrors();
  const publishingAction = useSocialPostPublishingAction();
  const triggerPrevious = useTriggerPrevious();
  const dispatch = useAppDispatch();

  const {pushStep, updateWindowHistoryState, popPreviousStep} = useWizardNavigationHistory({
    wizardStep,
    post: post ?? null,
  });

  const [isLoadingEmbeddedEditor, setIsLoadingEmbeddedEditor] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const [areAllPreviewsUpToDate, setAreAllPreviewsUpToDate] = useState(false);
  const [designsToPublish, setDesignsToPublish] = useState<DesignSelection[]>([]);
  const [isRedirectingToMyStuff, setIsRedirectingToMyStuff] = useState(false);

  /** A logged-out visitor previews a public template's stored graphic; there is no regeneration to wait for. */
  const arePreviewsReadyForDisplay = (): boolean => {
    return areAllPreviewsUpToDate || !isUserLoggedIn();
  };

  const {containerQueryParamProps, containerRef, hasContainerQueryInitialized} = useSocialMediaWizardContainerQuery();
  const myRef: RefObject<HTMLElement | null> = containerRef as RefObject<HTMLElement | null>;
  const params = useContainerQuery(myRef, containerQuery);
  const connectedAccounts = useWizardConnectedAccounts();
  const areAccountsLoading = useAreAccountsLoading();
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const {isContentHeadingHidden, isContentScrolled} = useWizardScrollState(contentContainerRef, wizardStep);
  const hasReachedReviewStep = useHasReachedReviewStep(wizardStep);
  const designsToPublishTo = useDesignsToPublish();
  const postPublishingParams = useWizardPublishingParams();
  const designsGraphicsMetaMap = useWizardDesignsGraphicMetaMap();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const socialPost = useSocialMediaWizardPost();
  const postId = usePostId();
  const designSelectorTab = useDesignSelectorTab();
  const connectedAccountIds = connectedAccounts.map((account) => {
    return account.id;
  });
  const haveAllPostsPublished = useHaveAllPostsPublished();
  const haveAllPostsSettled = useHaveAllPostsSettled();
  const publishedSlotCounts = usePublishedSlotCounts();
  const publishingStatusForAllAccounts = usePublishingStatusForAllAccounts();
  const accountsToPublishTo = useAccountsToPublishTo();
  const areCaptionsBeingAutoGenerated = useAreCaptionsBeingAutoGenerated();
  const publishPhase = useSocialMediaPublishPhase();

  const {onSaveAsDraft} = useWizardDraftScheduleActions({
    socialPost,
    accountIdsToPublishTo,
    captionErrors,
    titleErrors,
  });

  useAiCaptionWizardSetup({connectedAccountIds, postId});

  const {updatePublishingDesignsMeta, updateDesignsMetaOnClosingEmbeddedEditor, fetchGraphicsMetaForAnonymousTemplates, checkUpdateInGeneratedPreviewsStatus} = useDesignMetaSync({
    designsToPublishTo,
    postId: postId ?? undefined,
    accountIdsToPublishTo,
  });

  const {onClickEditDesign, onEmbeddedEditorOpened, onEmbeddedEditorForTemplateClosed, triggerEditDesignsFlow} = useEmbeddedEditorFlow({
    designsToPublishTo,
    designsGraphicsMetaMap,
    design,
    updatePublishingDesignsMeta,
    updateDesignsMetaOnClosingEmbeddedEditor,
    setIsPreviewLoading,
    setIsLoadingEmbeddedEditor,
  });

  const {confirmLeavingWithUnsavedChanges} = useWizardWindowEvents({
    onBack: (): void => {
      onBackButtonClick();
    },
  });

  const isMobileView = (): boolean => {
    return params.small || params.xsmall;
  };

  const {showPulsatingDotForPreviewCTA, resetPreviewCTAPulsesStates, triggerPreviewCTAPulsesIfNeeded, hidePulsatingDotForPreviewCTAIfNeeded} = useWizardCoverCtaPulse({
    currentStep: wizardStep,
    isMobileScreen: isMobileView(),
    designGraphicsMetaMap: designsGraphicsMetaMap,
    accountsToPublishTo,
  });

  const setWizardStep = (step: SocialMediaWizardStep | null): void => {
    if (step) {
      dispatch(updateWizardStep(step));
    }
  };

  const setDetailsForSchedulingIfNeeded = (): void => {
    const prefilledDate = window.PMW.getCacheItem(CACHE_KEY_FOR_SCHEDULING) as string;

    if (!prefilledDate) {
      return;
    }

    const dateTime = getSchedulingDateForPrefill(prefilledDate);
    const timeZoneOffset = dateTime.getTimezoneOffset();

    if (socialPost !== undefined) {
      const newPost: SocialPost = {
        ...socialPost,
        state: PostState.SCHEDULE_READY,
        publishOn: convertDateToUTCTimestamp(dateTime),
        timeZoneOffset,
      };

      dispatch(updatePost(newPost));
    }
  };

  useDidUpdateEffect(() => {
    if (designsToPublishTo.length === 0 && post) {
      setDesignsToPublish(post.designs);
      dispatch(updateDesignsToPublish(post.designs));

      return;
    }

    if (isUserLoggedIn() && connectedAccountIds.length > 0) {
      void updatePublishingDesignsMeta();
    } else if (template || designsToPublishTo.length > 0) {
      void fetchGraphicsMetaForAnonymousTemplates(designsToPublishTo[0] ?? template);
    }
  }, [designsToPublishTo, connectedAccountIds.length]);

  const onPreviewLoaded = (): void => {
    setIsPreviewLoading(false);
  };

  const getDesignActionsCtaSlot = (): ReactElement | null => {
    if (isPreviewLoading || isSelectTemplateFlow) {
      return null;
    }

    return (
      <WizardDesignActions
        onDesignsStaged={setDesignsToPublish}
        onEditDesign={onClickEditDesign}
        onEmbeddedEditorOpened={onEmbeddedEditorOpened}
        isLoadingEmbeddedEditor={isLoadingEmbeddedEditor}
        setIsPreviewLoading={setIsPreviewLoading}
        setIsLoadingEmbeddedEditor={setIsLoadingEmbeddedEditor}
      />
    );
  };

  const linkSocialPostToEvent = (): void => {
    const idPost = wizardPost ? wizardPost.id : null;
    if (!eventHashId) {
      return;
    }

    void window.PMW.write(window.PMW.util.site_url('socialmedia/linkSocialPostToEvent'), {
      idPost,
      eventHashId,
    });
  };

  const doUpdateNextButtonIfNeeded = (step: SocialMediaWizardStep): void => {
    if (step === SocialMediaWizardStep.PUBLISH) {
      return;
    }

    const config = getNextButtonConfig({step, post: wizardPost, isSelectTemplateFlow, stepOrder});
    if (config.text !== nextButtonText) {
      dispatch(updateNextButtonText(config.text));
      dispatch(updateNextButtonIcon(config.icon));
    }
  };

  const onNext: VoidFunction = (): void => {
    initPanelSpecificHandlersOnNext();
    const nextStep = getNextWizardStep(post, accounts, wizardStep, {
      isGalleryFlow: isSelectTemplateFlow,
      emphasizedPlatform,
      template,
      stepOrder,
    });

    hideMobilePageSelectorPopup();

    if (nextStep) {
      initPanelSpecificHandlersOnCurrentStep(nextStep);

      const isAnyAccountErrorProne = getTitleErrorAccountId(accountIdsToPublishTo, titleErrors) !== null || getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors) !== null;

      if (isAnyAccountErrorProne && nextStep === SocialMediaWizardStep.PUBLISH) {
        return;
      }

      setWizardStep(nextStep);
      setDetailsForSchedulingIfNeeded();
    }
  };

  const resetContentScrollPosition = (): void => {
    const scrollingContainer = contentContainerRef.current;

    if (scrollingContainer) {
      jumpContainerToTop(scrollingContainer);
    }
  };

  const scrollToAccountSection = (accountId: SocialMediaAccountId | null): void => {
    const scrollingContainer = contentContainerRef.current;

    if (!accountId || !scrollingContainer) {
      return;
    }

    const element = document.querySelectorAll(`.${PUBLISH_PARAM_HANDLER_CLASS}[data-key="${getKeyForPublishParams(accountId)}"]`)[0];

    scrollToPublishParams(scrollingContainer, element);
  };

  const initPanelSpecificHandlersOnCurrentStep = (currentStep: SocialMediaWizardStep): void => {
    const captionErrorAccount = getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors);
    const titleErrorAccount = getTitleErrorAccountId(accountIdsToPublishTo, titleErrors);
    const isAnyAccountErrorProne = captionErrorAccount !== null || titleErrorAccount !== null;

    if (currentStep === SocialMediaWizardStep.PUBLISH && isAnyAccountErrorProne) {
      dispatch(updatePublishingAction(undefined));
      scrollToAccountSection(captionErrorAccount ?? titleErrorAccount);
      return;
    }

    if (currentStep === SocialMediaWizardStep.ADD_DETAILS) {
      dispatch(initPublishingParams());
      dispatch(syncPublishingParams());
      void dispatch(
        savePreferredProfileSelectionUser({
          socialMediaAccountIds: accountIdsToPublishTo,
          idPost: wizardPost?.id,
        })
      );
    }

    if (currentStep === SocialMediaWizardStep.PUBLISH) {
      if (publishingAction === undefined && !isAnyAccountErrorProne) {
        dispatch(updatePublishingAction(SocialPostPublishingAction.PUBLISH));
      }
    } else {
      dispatch(updatePublishingAction(undefined));
    }
  };

  const doToggleNextButtonVisibility = (): void => {
    dispatch(showNextButton());
  };

  const hideMobilePageSelectorPopup = (): void => {
    document.querySelector('.mobile-pages-view')?.classList.add('_hidden');
  };

  const initPanelSpecificHandlersOnNext = (): void => {
    if (wizardStep === SocialMediaWizardStep.SELECT_DESIGN) {
      const designs = designsToPublish;
      dispatch(showNextButton());

      if (designs.length > 0) {
        if (!isSelectTemplateFlow) {
          trackPublishingGA4Events(GA4EventName.SOCIALWIZARD_MYDESIGNSELECTED);
        }

        dispatch(updateDesignsToPublish(designs));
      }
    }

    if (wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS) {
      dispatch(resetAccountResizeStatusForDesignsMap());
    }
  };

  const resetSocialPostCoverInPublishingParamsIfNeeded = (newWizardStep: SocialMediaWizardStep): void => {
    const isPreviouslySelectedPostSingleGraphic = designsToPublishTo.length === 1;
    const shouldResetCoverForReselectedDesign = newWizardStep === SocialMediaWizardStep.SELECT_DESIGN && isUserLoggedIn() && isPreviouslySelectedPostSingleGraphic;

    if (shouldResetCoverForReselectedDesign) {
      dispatch(resetSocialPostCoverForAllPublishingParams());
    }
  };

  const cleanupTemplateFlowIfNeeded = (newWizardStep: SocialMediaWizardStep): void => {
    if (newWizardStep === SocialMediaWizardStep.SELECT_DESIGN && isUserLoggedIn() && isTemplateSelected) {
      setDesignsToPublish([]);
      dispatch(updateDesignsToPublish([]));
      dispatch(updateIsTemplateSelected(false));
    }
  };

  const isReturningFromPublishScreen = (): boolean => {
    return publishingAction !== undefined;
  };

  const getStepToReturnTo = (poppedStep: SocialMediaWizardStep | undefined): SocialMediaWizardStep | undefined => {
    if (isReturningFromPublishScreen()) {
      return SocialMediaWizardStep.ADD_DETAILS;
    }

    return poppedStep;
  };

  const resetPublishingStateWhenLeavingPublishScreen = (): void => {
    if (isReturningFromPublishScreen()) {
      dispatch(resetStateToDraft());
    }
  };

  const onBack: VoidFunction = (): void => {
    const targetStep: SocialMediaWizardStep | undefined = getStepToReturnTo(popPreviousStep());

    if (targetStep) {
      resetPublishingStateWhenLeavingPublishScreen();
      resetSocialPostCoverInPublishingParamsIfNeeded(targetStep);
      cleanupTemplateFlowIfNeeded(targetStep);
      setWizardStep(targetStep);
    } else {
      navigateOutOfWizard();
    }
  };

  const onBackButtonClick = (): void => {
    if (didAllAccountsErrorOut(accountIdsToPublishTo, publishingStatusForAllAccounts)) {
      dispatch(resetStateToDraft());
    }

    onBack();
  };

  const savePostAsDraftIfNeeded = (): void => {
    if (!publishingAction || publishingAction === SocialPostPublishingAction.SAVE) {
      dispatch(updatePublishingAction(SocialPostPublishingAction.SAVE));
      dispatch(updateTriggerNext());
    }
  };

  const onLogoClick = (): void => {
    if (!confirmLeavingWithUnsavedChanges()) {
      return;
    }

    setIsRedirectingToMyStuff(true);
    showLoading(MY_STUFF_REDIRECT_LOADING_KEY, {text: window.i18next.t('pmwjs_redirecting_to_my_stuff')});
    savePostAsDraftIfNeeded();

    redirectToMyStuffAfterDelay(SAVE_DRAFT_BEFORE_REDIRECT_DELAY_MS);
  };

  const onDesignListChange = (designs: DesignSelection[]): void => {
    setDesignsToPublish(designs);
  };

  const selectDesignsAndContinue = (designs: DesignSelection[]): void => {
    setDesignsToPublish(designs);
    dispatch(updateDesignsToPublishAndTriggerNext(designs));
  };

  const onGalleryDesignsSelected = (templateDesigns: DesignSelection[]): void => {
    trackPublishingGA4Events(GA4EventName.SOCIALWIZARD_TEMPLATESELECTED);
    dispatch(updateIsTemplateSelected(true));
    selectDesignsAndContinue(templateDesigns);
  };

  const openGalleryUploadDesignsModal = (): void => {
    window.PMW.triggerCreateDesignFromUserUpload(getWizardUploadDesignsModalOpts({...WIZARD_UPLOAD_MEDIA_GA4_CALLBACKS, onDesignsCreated: selectDesignsAndContinue}));
  };

  const openGalleryCreateWithAiChat = (): void => {
    openSocialMediaWizardAiChatModal({source: AiChatSource.SOCIAL_MEDIA_WIZARD_TEMPLATES, onDesignsCreated: selectDesignsAndContinue});
  };

  const onGalleryUploadMediaClick = (): void => {
    if (isUserLoggedIn()) {
      openGalleryUploadDesignsModal();
      return;
    }

    openSocialMediaWizardAuthenticateModal(openGalleryUploadDesignsModal);
  };

  const onCancel = (): void => {
    dispatch(updateTriggerPrevious());
  };

  const getSocialWizardPublishingPanelHeading = (): string => {
    return getPublishingPanelHeading({publishingAction, haveAllPostsPublished, haveAllPostsSettled, publishedSlotCounts});
  };

  const defaultPreviewContent: ReactElement = (
    <WizardPreviewPanel
      wizardStep={wizardStep}
      designsToPublishTo={designsToPublishTo}
      postPublishingParams={postPublishingParams}
      designsGraphicsMetaMap={designsGraphicsMetaMap}
      isSelectTemplateFlow={isSelectTemplateFlow}
      isMobileView={isMobileView()}
      isPreviewLoading={isPreviewLoading}
      areAllPreviewsUpToDate={arePreviewsReadyForDisplay()}
      ctaSlot={getDesignActionsCtaSlot()}
      onDesignPreviewClick={triggerEditDesignsFlow}
      onPreviewLoaded={onPreviewLoaded}
    />
  );

  const getFallbackPanelDescriptor = (): WizardPanelDescriptor => {
    return {
      mainContent: <SocialAccountButtons />,
      header: '',
      previewContent: defaultPreviewContent,
    };
  };

  const getWizardPanelDescriptor = (): WizardPanelDescriptor => {
    if (!wizardStep) {
      return getFallbackPanelDescriptor();
    }

    switch (wizardStep) {
      case SocialMediaWizardStep.SELECT_DESIGN: {
        const isAnonymousGalleryFlow = isSelectTemplateFlow && !isUserLoggedIn();
        const selectDesignMainContent = isAnonymousGalleryFlow ? (
          <DesignGallery
            onDesignsSelected={onGalleryDesignsSelected}
            onUploadMediaClick={onGalleryUploadMediaClick}
            onCreateWithAiClick={openGalleryCreateWithAiChat}
            height={getDesignGalleryHeightForSelectDesignStep(isMobileView())}
            startingPageURI={galleryStartingURI ?? getStartingURIForGallery()}
          />
        ) : (
          <WizardDesignSelectorPanel
            designsToPublish={designsToPublish}
            onDesignListChange={onDesignListChange}
            onDesignsStaged={setDesignsToPublish}
            designSelectorTab={designSelectorTab}
            galleryStartingURI={galleryStartingURI}
          />
        );
        return {
          mainContent: selectDesignMainContent,
          header: window.i18next.t('pmwjs_start_by_selecting_design'),
          previewContent: undefined,
        };
      }

      case SocialMediaWizardStep.ACCOUNT_CONNECTION: {
        const accountConnectionMainContent = accountTypeToConnect ? <SocialAccountConnection accountTypeToConnect={accountTypeToConnect} /> : <SocialAccountButtons />;
        return {
          mainContent: accountConnectionMainContent,
          header: accountTypeToConnect ? window.i18next.t('pmwjs_connect_your_accounts') : '',
          previewContent: defaultPreviewContent,
        };
      }

      case SocialMediaWizardStep.SELECT_ACCOUNTS: {
        const accountsAvailableForWizard = getAccountsAvailableForWizard(connectedAccounts, isGoogleBusinessProfileEnabled);
        const hasNoAccountsAvailableForWizard = !areAccountsLoading && accountsAvailableForWizard.length === 0;
        return {
          mainContent: <SocialAccountsList />,
          header: hasNoAccountsAvailableForWizard ? window.i18next.t('pmwjs_connect_your_accounts') : window.i18next.t('pmwjs_select_accounts_heading'),
          previewContent: defaultPreviewContent,
        };
      }

      case SocialMediaWizardStep.EDIT_TEMPLATE:
        return {
          mainContent: (
            <TemplateEditorPanel
              onSave={(posterHashId) => {
                void onEmbeddedEditorForTemplateClosed(posterHashId);
              }}
              onClose={onCancel}
            />
          ),
          header: '',
          previewContent: undefined,
        };

      case SocialMediaWizardStep.ADD_DETAILS: {
        return {
          mainContent: <SocialMediaPostDetails size={getWizardContentSize(params)} scrollingContainerRef={contentContainerRef} key={'details-panel'} />,
          header: window.i18next.t('pmwjs_review_and_post'),
          previewContent: undefined,
        };
      }

      case SocialMediaWizardStep.PUBLISH: {
        if (accountIdsToPublishTo.length > 0) {
          return {
            mainContent: <SocialMediaPostDetails size={getWizardContentSize(params)} scrollingContainerRef={contentContainerRef} key={'publishing-panel'} />,
            header: getSocialWizardPublishingPanelHeading(),
            previewContent: undefined,
          };
        }
        return getFallbackPanelDescriptor();
      }

      default:
        return getFallbackPanelDescriptor();
    }
  };

  const {mainContent, header, previewContent} = getWizardPanelDescriptor();

  useUpdatePublishingParams();

  useDidUpdateEffect(() => {
    pushStep();
    resetContentScrollPosition();
    doUpdateNextButtonIfNeeded(wizardStep);
    resetPreviewCTAPulsesStates();
    triggerPreviewCTAPulsesIfNeeded();
  }, [wizardStep]);

  useEffect(() => {
    doToggleNextButtonVisibility();

    if (wizardStep === SocialMediaWizardStep.SELECT_DESIGN) {
      if (designsToPublish.length === 0) {
        dispatch(disableNextButton());
      }

      setDesignsToPublish(designsToPublishTo);
    }

    // codeReviewAhsan: review this check if needed
    if (wizardStep === SocialMediaWizardStep.ADD_DETAILS) {
      if (isEventsSocialPostingFlow()) {
        linkSocialPostToEvent();
      }
    }

    updateWindowHistoryState(wizardStep);
  }, [wizardStep]);

  useEffect(() => {
    if (wizardStep !== SocialMediaWizardStep.SELECT_ACCOUNTS) {
      return;
    }

    setIsPreviewLoading(true);

    if (designsToPublishTo.length === 0 || !isUserLoggedIn()) {
      return;
    }

    let isCancelled = false;
    void checkUpdateInGeneratedPreviewsStatus(designsToPublishTo, () => isCancelled).then((allGenerated) => {
      if (allGenerated && !isCancelled) {
        setAreAllPreviewsUpToDate(true);
      }
    });

    return (): void => {
      isCancelled = true;
      document.dispatchEvent(new Event(window.PMW.getPollingStopEvent(PREVIEW_GENERATION_POLLING_EVENT_ID)));
    };
  }, [wizardStep, designsToPublishTo]);

  useDidUpdateEffect(() => {
    setAreAllPreviewsUpToDate(false);
  }, [designsToPublishTo]);

  const toggleNextButtonByAccountsConnected = (): void => {
    if (connectedAccounts.length === 0) {
      dispatch(disableNextButton());
    } else if (accountIdsToPublishTo.length !== 0) {
      dispatch(enableNextButton());
    }
  };

  useEffect(() => {
    toggleNextButtonByAccountsConnected();
  }, [connectedAccounts]);

  useWizardInit({
    post,
    accounts,
    accountIdsForPost,
    design,
    template,
    isGalleryFlow,
    isSelectTemplateFlow,
    emphasizedPlatform,
    hasSeenOnboarding,
    hasSeenConfetti,
    hasUserPublishedBefore,
    isSocialMediaWizardAiCaptionsEnabled,
    isGoogleBusinessProfileEnabled,
    numParentAccountsConnected,
    stepOrder,
    setDesignsToPublish,
  });

  useTrackWizardStepViewed({wizardStep, hasStartedLoggedOut, hasResolvedStartStep});

  useDidUpdateEffect(() => {
    onNext();
  }, [triggerNext]);

  useDidUpdateEffect(() => {
    onBack();
  }, [triggerPrevious]);

  if (wizardStep === SocialMediaWizardStep.ACCOUNT_CONNECTION && !accountTypeToConnect) {
    openErrorModal({message: window.i18next.t('pmwjs_error_message')});
  }

  const getFooter = (): ReactElement | null => {
    if (publishPhase === SocialMediaPublishPhase.TERMINAL) {
      return isMobileView() ? <WizardTerminalMobileFooter /> : null;
    }

    return (
      <SocialMediaWizardFooter
        isSmallScreen={isMobileView()}
        hasReachedReviewStep={hasReachedReviewStep}
        onNext={onNext}
        onBack={onBackButtonClick}
        currentSelectedDesigns={designsToPublish}
      />
    );
  };

  const shouldHideBackButton = (): boolean => {
    if (isRedirectingToMyStuff) {
      return true;
    }

    if (publishPhase === SocialMediaPublishPhase.TERMINAL) {
      return true;
    }

    if (publishPhase === SocialMediaPublishPhase.IN_FLIGHT) {
      return false;
    }

    return wizardStep === SocialMediaWizardStep.PUBLISH && !didAllAccountsErrorOut(accountIdsToPublishTo, publishingStatusForAllAccounts);
  };

  const getHeaderBackClickHandler = (): VoidFunction | undefined => {
    if (shouldHideBackButton()) {
      return undefined;
    }

    return onBackButtonClick;
  };

  const onMobilePreviewButtonClick = (): void => {
    hidePulsatingDotForPreviewCTAIfNeeded();

    if (wizardStep === SocialMediaWizardStep.ADD_DETAILS) {
      openSocialPostAccountPreviewsModal();
      return;
    }

    openSocialPostPreviewModal({
      isSelectTemplateFlow,
      areAllPreviewsUpToDate: arePreviewsReadyForDisplay(),
      onEditDesign: onClickEditDesign,
      onEmbeddedEditorOpened,
      onDesignsStaged: setDesignsToPublish,
    });
  };

  const getMobilePreviewButtonDot = (): ReactElement | null => {
    if (!showPulsatingDotForPreviewCTA) {
      return null;
    }

    return <PulsatingDot containerClassname={styles.mobilePreviewButtonDot} />;
  };

  const getMobilePreviewButton = (): ReactElement => {
    return (
      <div className={`_position-relative ${styles.mobilePreviewButtonContainer}`}>
        {getMobilePreviewButtonDot()}
        <Button onClick={onMobilePreviewButtonClick} size={ButtonSize.MEDIUM} type={ButtonType.TERTIARY} text={window.i18next.t('pmwjs_preview')} icon="icon-eye" />
      </div>
    );
  };

  const getMobileHeaderAction = (): ReactElement | null => {
    if (hasMobilePreviewAction(wizardStep)) {
      return getMobilePreviewButton();
    }

    return null;
  };

  const getDesktopSaveAsDraftButton = (): ReactElement | null => {
    if (wizardStep !== SocialMediaWizardStep.ADD_DETAILS) {
      return null;
    }

    return (
      <Button
        type={ButtonType.TERTIARY}
        size={ButtonSize.MEDIUM}
        icon="icon-save"
        iconClassName={styles.saveDraftButtonIcon}
        text={window.i18next.t('pmwjs_save_draft')}
        onClick={onSaveAsDraft}
        disabled={areCaptionsBeingAutoGenerated}
      />
    );
  };

  const getHeaderActionsDivider = (leadingAction: ReactElement | null, trailingAction: ReactElement | null): ReactElement | null => {
    if (!leadingAction || !trailingAction) {
      return null;
    }

    return <span aria-hidden className={styles.headerActionsDivider} />;
  };

  const getShareInTeamToggler = (): ReactElement | null => {
    if (!isUserPremium() || !wizardPost || !hasReachedReviewStep) {
      return null;
    }

    return <ShareSocialPostInTeamToggler post={wizardPost} gaEvent={GA4EventName.COLLABORATION_SOCIALPOST_SHAREINTEAM_WIZARD} />;
  };

  const getDisabledSaveAsDraftButton = (): ReactElement => {
    return <Button disabled type={ButtonType.TERTIARY} size={ButtonSize.MEDIUM} icon="icon-save" iconClassName={styles.saveDraftButtonIcon} text={window.i18next.t('pmwjs_save_draft')} />;
  };

  const getWizardExitButton = (): ReactElement => {
    return <Button type={ButtonType.SECONDARY} size={ButtonSize.MEDIUM} text={getWizardExitLabel()} onClick={exitWizardToOriginSurface} />;
  };

  const getHeaderRightSlot = (): ReactElement | null => {
    if (isMobileView()) {
      return getMobileHeaderAction();
    }

    if (publishPhase === SocialMediaPublishPhase.TERMINAL) {
      return getWizardExitButton();
    }

    if (publishPhase === SocialMediaPublishPhase.IN_FLIGHT) {
      return getDisabledSaveAsDraftButton();
    }

    const shareInTeamToggler = getShareInTeamToggler();
    const saveAsDraftButton = getDesktopSaveAsDraftButton();

    return (
      <>
        {shareInTeamToggler}
        {getHeaderActionsDivider(shareInTeamToggler, saveAsDraftButton)}
        {saveAsDraftButton}
      </>
    );
  };

  const getMobileHeaderTitle = (): string | undefined => {
    if (!isMobileView()) {
      return undefined;
    }

    if (wizardStep === SocialMediaWizardStep.SELECT_DESIGN) {
      return window.i18next.t('pmwjs_choose_what_youd_like_to_post');
    }

    return header;
  };

  const getContainerClasses = (): string => {
    const size = getWizardContentSize(params);
    const isMobile = size === SocialMediaWizardContentSize.XSMALL || size === SocialMediaWizardContentSize.SMALL;

    if (isMobile) {
      const bottomMarginClass = wizardStep === SocialMediaWizardStep.SELECT_DESIGN ? '' : 'spacing-m-b-10';
      return `_full-height _full-width ${bottomMarginClass}`;
    }

    return `_full-width`;
  };

  const socialMediaWizardContainerQueriesContextValues = useMemo(() => {
    return {
      isExtraSmallScreen: containerQueryParamProps.isExtraSmallScreen,
      isSmallScreen: containerQueryParamProps.isSmallScreen,
      isMediumScreen: containerQueryParamProps.isMediumScreen,
      isLargeScreen: containerQueryParamProps.isLargeScreen,
      isExtraLargeScreen: containerQueryParamProps.isExtraLargeScreen,
    };
  }, [containerQueryParamProps]);

  const getWizardHeader = (): ReactElement => {
    return (
      <SocialMediaWizardHeader
        isSmallScreen={isMobileView()}
        onBackClick={getHeaderBackClickHandler()}
        isBackDisabled={publishPhase === SocialMediaPublishPhase.IN_FLIGHT}
        onLogoClick={onLogoClick}
        hasShadow={isContentScrolled}
        desktopTitle={header}
        isDesktopTitleVisible={isContentHeadingHidden}
        mobileTitle={getMobileHeaderTitle()}
        rightSlot={getHeaderRightSlot()}
      />
    );
  };

  const getWizardContentForContainer = (): ReactElement => {
    if (!hasContainerQueryInitialized) {
      return <CircularProgressLoader classes="-for-mystuff-content" />;
    }

    return (
      <>
        <SocialMediaWizardContent size={getWizardContentSize(params)} heading={header} mainContent={mainContent} previewContent={previewContent} />
        <SocialMediaWizardOnboarding />
      </>
    );
  };

  const getScrollableContentColumn = (): ReactElement => {
    return (
      <div ref={contentContainerRef} className={styles.contentContainer}>
        <SocialMediaWizardContainerQueriesContext.Provider value={socialMediaWizardContainerQueriesContextValues}>
          <div ref={containerRef} className={getContainerClasses()}>
            {getWizardContentForContainer()}
          </div>
        </SocialMediaWizardContainerQueriesContext.Provider>
      </div>
    );
  };

  return (
    <div className={styles.pageContainer}>
      {getWizardHeader()}
      {getScrollableContentColumn()}
      {getFooter()}
    </div>
  );
}
